import FingerprintJS, { ExtendedGetResult } from '@fingerprintjs/fingerprintjs-pro';
import { customAlphabet } from 'nanoid';
import React, { ReactNode, useEffect } from 'react';
import { Cookies } from 'react-cookie';
import { create } from 'zustand';
import { VisitorTracking } from '../types/AnalyticsTracking';
import BrandclubStoreConfigUtils from '../utils/brandclubStoreConfigUtils';
import * as AnalyticsService from './AnalyticsService';
import { AnalyticsTrackingEvent } from './events';
import { timeoutPromise } from './utils';

const useFingerprintJSStore = create<{
  visitorId?: string;
  visitorSessionId?: string;
  visitorTracking?: VisitorTracking;
  setVisitorTracking: (visitorTracking: VisitorTracking) => void;
  fpLoadPromise?: Promise<any>;
  setFpLoadPromise: (fpLoadPromise?: Promise<any>) => void;
}>((set) => ({
  visitorId: undefined,
  setVisitorTracking: (visitorTracking: VisitorTracking) =>
    set({
      visitorId: visitorTracking.uniqueVisitorId ?? undefined,
      visitorSessionId: visitorTracking.visitorSessionId,
      visitorTracking,
    }),
  visitorTracking: undefined,
  fpLoadPromise: undefined,
  setFpLoadPromise: (fpLoadPromise?: Promise<any>) => set({ fpLoadPromise }),
}));

const nanoid = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyz', 10);
const FINGERPRINT_JS_BROWSER_API_KEY = 'K1ACT61coP6ep24p7Bwr';
const FINGERPRINT_JS_USE_PROXY_FOR_ENDPOINT = true;
// const FINGERPRINT_JS_SERVER_API_KEY = "***HAVE_TO_GET_IT***";
export const FingerprintJSContext = React.createContext<VisitorTracking | undefined>(undefined);

export const useFingerprintJSContext = () => React.useContext(FingerprintJSContext);

const DEBUG = false;
const vsCookieName = '_vs_id';
const fpResultCacheKey = `fp:${vsCookieName}:result`;
const cookies = new Cookies();

let visitorSessionId = cookies.get(vsCookieName);
if (!visitorSessionId) {
  visitorSessionId = nanoid();
  cookies.set(vsCookieName, visitorSessionId, {
    domain: '.' + BrandclubStoreConfigUtils.getAuthDomain(BrandclubStoreConfigUtils.getAppStage()),
    maxAge: 60 * 60 * 24, // 1 day
    path: '/',
    secure: true,
    httpOnly: false,
    sameSite: 'lax',
  });
}

export const FingerprintJSProvider = ({
  children,
  hostDomainName,
}: {
  children: ReactNode;
  hostDomainName: string;
}) => {
  const FINGERPRINT_JS_BROWSER_ENDPOINT = hostDomainName;
  const FINGERPRINT_JS_AGENT_ENDPOINT = hostDomainName;
  const [visitorTracking, setVisitorTracking, fpLoadPromise, setFpLoadPromise] = useFingerprintJSStore((state) => [
    state.visitorTracking,
    state.setVisitorTracking,
    state.fpLoadPromise,
    state.setFpLoadPromise,
  ]);

  useEffect(() => {
    const referrer = document.referrer;
    if (!visitorTracking?.uniqueVisitorId || !visitorTracking?.isResolved) {
      let fpResultValue = window.localStorage.getItem(fpResultCacheKey);
      if (!fpResultValue) {
        // Try to get from sessionStorage if not in localStorage
        fpResultValue = window.sessionStorage.getItem(fpResultCacheKey);
        if (fpResultValue) {
          // Cache in localStorage for future visits
          window.localStorage.setItem(fpResultCacheKey, fpResultValue);
          window.sessionStorage.removeItem(fpResultCacheKey); // Remove from sessionStorage
        }
      }
      if (fpResultValue) {
        try {
          const { visitorSessionId: cachedSessionId, result } = JSON.parse(fpResultValue);
          // Check if the cached result is for the current session
          if (visitorSessionId === cachedSessionId) {
            setVisitorTracking({
              visitorSessionId,
              uniqueVisitorId: result.visitorId,
              referrer,
              fingerprintResult: result,
              isResolved: true,
            });
            // eslint-disable-next-line no-console
            DEBUG && console.log('FingerprintJS loaded from cache', visitorSessionId);
            return;
          }
        } catch (e) {
          console.error(`Error parsing cached fingerprint result: ${e}`);
        }
      }
      if (fpLoadPromise) {
        // eslint-disable-next-line no-console
        DEBUG && console.log('FJs already loading');
        return;
      }
      const fpLoadPromiseBuilder = () => {
        // eslint-disable-next-line no-console
        DEBUG && console.log('FJs loading', visitorSessionId);
        const isBot = /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator?.userAgent);
        if (isBot) {
          const result = {
            visitorId: 'bc-bot:' + visitorSessionId,
            bot: true,
          };
          setVisitorTracking({
            visitorSessionId,
            uniqueVisitorId: result.visitorId,
            referrer,
            fingerprintResult: result as any,
            isResolved: true,
          });
          window.localStorage.setItem(fpResultCacheKey, JSON.stringify({ visitorSessionId, result }));
          setFpLoadPromise(undefined);
          return Promise.resolve();
        }
        return timeoutPromise(2e3, 'FJs.load', () =>
          FingerprintJS.load({
            scriptUrlPattern:
              // "https://fpnpmcdn.net/v<version>/<apiKey>/loader_v<loaderVersion>.js",
              `${FINGERPRINT_JS_AGENT_ENDPOINT}/Jzq31cA2yOXuX74326KI33y/dXtUA01lo82H22P4lNPkV46S?apiKey=<apiKey>&version=3&loaderVersion=3.8.1`,
            apiKey: FINGERPRINT_JS_BROWSER_API_KEY,
            // This works but goes through our proxy but FINGERPRINT_JS_BROWSER_ENDPOINT directly works as well
            endpoint: FINGERPRINT_JS_USE_PROXY_FOR_ENDPOINT
              ? `${FINGERPRINT_JS_AGENT_ENDPOINT}/Jzq31cA2yOXuX74326KI33y/5ksfEIl7Q0t2f8es3L1oIS99`
              : FINGERPRINT_JS_BROWSER_ENDPOINT,
          }),
        )
          .then((fp) => {
            // eslint-disable-next-line no-console
            DEBUG && console.log('Fjs loaded', visitorSessionId);
            return fp
              .get({ extendedResult: true })
              .then((result: ExtendedGetResult) => {
                // console.log('Fjs result', result);
                setVisitorTracking({
                  visitorSessionId,
                  uniqueVisitorId: result.visitorId,
                  referrer,
                  fingerprintResult: result,
                  isResolved: true,
                });
                window.localStorage.setItem(fpResultCacheKey, JSON.stringify({ visitorSessionId, result }));
              })
              .catch((e: any) => {
                setVisitorTracking({
                  visitorSessionId,
                  uniqueVisitorId: 'bc-session:' + visitorSessionId,
                  referrer,
                  isResolved: false,
                });
                DEBUG && console.error(`Error getting FJs result: ${e}`);
                AnalyticsService.track(AnalyticsTrackingEvent.FINGERPRINTJS_ERROR, {
                  error: e,
                  type: 'get',
                });
              });
          })
          .catch((e) => {
            setVisitorTracking({
              visitorSessionId,
              uniqueVisitorId: 'bc-session:' + visitorSessionId,
              referrer,
              isResolved: false,
            });
            DEBUG && console.error(`Error loading FJs: ${e}`);
            AnalyticsService.track(AnalyticsTrackingEvent.FINGERPRINTJS_ERROR, {
              type: 'load',
              error: e,
            });
          })
          .finally(() => {
            setFpLoadPromise(undefined);
          });
      };
      setFpLoadPromise(fpLoadPromiseBuilder());
    }
  }, [visitorTracking]);
  if (!visitorTracking) {
    return null;
  }
  // eslint-disable-next-line no-console
  DEBUG && console.log('FingerprintJSProvider', visitorTracking);
  return <FingerprintJSContext.Provider value={visitorTracking}>{children}</FingerprintJSContext.Provider>;
};

const useVisitorTracking = () => {
  const state = useFingerprintJSStore.getState();
  return [state.visitorTracking, state.setVisitorTracking] as const;
};

/**
 * Visitor ID is not immediately available and sometimes returns an empty string.
 * This function will keep checking for the visitorId until it is available or until a timeout is reached (10 seconds).
 * @returns Promise<string> - A promise that resolves to the visitorId
 */
const USER_ID_FETCH_TIMEOUT = 3000;
const asyncGetVisitorId = () => {
  const elapsed = Date.now();
  return new Promise<string>((resolve, reject) => {
    const checkTrackingInfo = () => {
      const [visitorTracking, setVisitorTracking] = useVisitorTracking();
      if (visitorTracking?.uniqueVisitorId) {
        resolve(visitorTracking.uniqueVisitorId);
        if (!visitorTracking.isResolved) {
          // to re-render the component which initiates the fingerprintjs load again
          setVisitorTracking({ ...visitorTracking });
        }
        return;
      }
      if (Date.now() - elapsed > USER_ID_FETCH_TIMEOUT) {
        reject('Timeout waiting for visitorId');
        return;
      }
      setTimeout(checkTrackingInfo, 100);
    };
    checkTrackingInfo();
  });
};

/**
 * Tries to get the visitorId and returns undefined if it fails.
 * @returns Promise<string | undefined> - A promise that resolves to the visitorId or undefined if it fails
 */
export const tryGetVisitorId = async () => {
  try {
    return await asyncGetVisitorId();
  } catch (e) {
    console.error(e);
    return undefined;
  }
};
